import {
  html,
  attr,
  prop,
  When,
  Unless,
  Ensure,
  Fragment,
  on,
  ForEach,
  Empty,
} from '@tempots/dom'
import {
  Button,
  Icon,
  Modal,
  Control,
  TextInput,
  PasswordInput,
  useForm,
  Tabs,
} from '@tempots/beatui'
import { Validation } from '@tempots/std'
import { PageLayout } from '@/components/layout'
import { LoadingState, ErrorState, toast } from '@/components/ui'
import { profile as profileService } from '@/services/gateway'
import type { UserProfile, LinkedIdentity } from '@/services/profile.service'

type TabType = 'profile' | 'security' | 'connected'

export function ProfilePage() {
  // State
  const activeTab = prop<TabType>('profile')
  const userProfile = prop<UserProfile | null>(null)
  const isLoading = prop(true)
  const error = prop<string | null>(null)

  // Form state for name editing
  const isSavingName = prop(false)

  // Form state for password
  const isSavingPassword = prop(false)

  // Form state for email change
  const isSavingEmail = prop(false)

  // Connected accounts state
  const identities = prop<LinkedIdentity[]>([])
  const isLoadingIdentities = prop(false)

  // Avatar state
  const isUploadingAvatar = prop(false)
  const isDeletingAvatar = prop(false)

  // Name form using useForm
  const nameForm = useForm({
    initialValue: { firstName: '', lastName: '', displayName: '' },
    onSubmit: async (value) => {
      const fn = value.firstName.trim()
      const ln = value.lastName.trim()
      const dn = value.displayName.trim()

      if (!fn || !ln) {
        return Validation.invalid({
          message: 'First name and last name are required',
        })
      }

      isSavingName.set(true)
      try {
        const updated = await profileService.updateName({
          firstName: fn,
          lastName: ln,
          displayName: dn || undefined, // Only send if provided, otherwise backend will generate
        })
        userProfile.set(updated)
        toast.success('Name updated successfully')
        return Validation.valid
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update name'
        toast.error(message)
        return Validation.invalid({ message })
      } finally {
        isSavingName.set(false)
      }
    },
  })

  // Password form using useForm
  const passwordForm = useForm({
    initialValue: { currentPassword: '', newPassword: '', confirmPassword: '' },
    onSubmit: async (value) => {
      const newPwd = value.newPassword
      const confirmPwd = value.confirmPassword
      const currentPwd = value.currentPassword

      if (!newPwd || !confirmPwd) {
        return Validation.invalid({
          message: 'Please fill in all password fields',
        })
      }

      if (newPwd !== confirmPwd) {
        return Validation.invalid({ message: 'Passwords do not match' })
      }

      const profile = userProfile.get()
      if (profile?.hasPassword && !currentPwd) {
        return Validation.invalid({ message: 'Current password is required' })
      }

      isSavingPassword.set(true)
      try {
        await profileService.setPassword({
          currentPassword: profile?.hasPassword ? currentPwd : undefined,
          newPassword: newPwd,
        })
        toast.success('Password updated successfully')
        // Reset form
        passwordForm.controller.change({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        })
        // Reload profile to update hasPassword status
        await loadProfile()
        return Validation.valid
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update password'
        toast.error(message)
        return Validation.invalid({ message })
      } finally {
        isSavingPassword.set(false)
      }
    },
  })

  // Email form using useForm
  const emailForm = useForm({
    initialValue: { newEmail: '', password: '' },
    onSubmit: async (value) => {
      const email = value.newEmail.trim()
      const pwd = value.password

      if (!email || !pwd) {
        return Validation.invalid({ message: 'Please fill in all fields' })
      }

      isSavingEmail.set(true)
      try {
        await profileService.initiateEmailChange({
          newEmail: email,
          password: pwd,
        })
        toast.success('Verification email sent to your new email address')
        emailForm.controller.change({ newEmail: '', password: '' })
        return Validation.valid
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to initiate email change'
        toast.error(message)
        return Validation.invalid({ message })
      } finally {
        isSavingEmail.set(false)
      }
    },
  })

  // Load profile on mount
  loadProfile()

  async function loadProfile() {
    isLoading.set(true)
    error.set(null)

    try {
      const profile = await profileService.getProfile()
      userProfile.set(profile)

      // Pre-populate name fields from profile or derive from displayName
      let firstName = profile.firstName || ''
      let lastName = profile.lastName || ''
      const displayName = profile.displayName || ''

      // If names are empty but displayName exists, try to split it
      if (!firstName && !lastName && displayName) {
        const parts = displayName.trim().split(/\s+/)
        if (parts.length >= 2) {
          firstName = parts[0]
          lastName = parts.slice(1).join(' ')
        } else if (parts.length === 1) {
          firstName = parts[0]
        }
      }

      nameForm.controller.change({ firstName, lastName, displayName })
    } catch (err) {
      error.set(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      isLoading.set(false)
    }
  }

  async function loadIdentities() {
    isLoadingIdentities.set(true)
    try {
      const response = await profileService.getLinkedIdentities()
      identities.set(response.identities || [])
    } catch {
      toast.error('Failed to load connected accounts')
    } finally {
      isLoadingIdentities.set(false)
    }
  }

  async function handleUnlinkIdentity(provider: string) {
    if (!confirm(`Are you sure you want to unlink ${provider}?`)) {
      return
    }

    try {
      await profileService.unlinkIdentity(provider)
      toast.success(`${provider} unlinked successfully`)
      await loadIdentities()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to unlink identity'
      )
    }
  }

  async function handleAvatarUpload(file: File) {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image')
      return
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('Image must be less than 5MB')
      return
    }

    isUploadingAvatar.set(true)
    try {
      const result = await profileService.uploadAvatar(file)
      const current = userProfile.get()
      if (current) {
        userProfile.set({ ...current, avatarUrl: result.avatarUrl })
      }
      toast.success('Avatar updated successfully')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload avatar'
      )
    } finally {
      isUploadingAvatar.set(false)
    }
  }

  async function handleAvatarDelete() {
    if (!confirm('Are you sure you want to remove your avatar?')) {
      return
    }

    isDeletingAvatar.set(true)
    try {
      await profileService.deleteAvatar()
      const current = userProfile.get()
      if (current) {
        userProfile.set({ ...current, avatarUrl: undefined })
      }
      toast.success('Avatar removed successfully')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to remove avatar'
      )
    } finally {
      isDeletingAvatar.set(false)
    }
  }

  // Handle tab changes with side effects
  function handleTabChange(tab: TabType) {
    activeTab.set(tab)
    if (tab === 'connected') {
      loadIdentities()
    }
  }

  function renderProfileSection() {
    const triggerAvatarInput = () => {
      const input = document.getElementById(
        'avatar-upload-input'
      ) as HTMLInputElement
      input?.click()
    }

    return html.div(
      attr.class('space-y-6'),

      // Avatar section
      html.div(
        attr.class('flex items-center gap-6'),
        // Avatar with overlay
        html.div(
          attr.class('relative group'),
          html.div(
            attr.class(
              'w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden'
            ),
            When(
              userProfile.map((p) => !!p?.avatarUrl),
              () =>
                html.img(
                  attr.class('w-full h-full object-cover'),
                  attr.src(userProfile.map((p) => p?.avatarUrl || '')),
                  attr.alt('Avatar')
                ),
              () =>
                html.span(
                  attr.class('text-3xl text-gray-400'),
                  userProfile.map((p) =>
                    (p?.displayName || p?.email || '?')[0].toUpperCase()
                  )
                )
            )
          ),
          // Upload overlay on hover
          html.div(
            attr.class(
              'absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer'
            ),
            on.click(() => triggerAvatarInput()),
            Icon({ icon: 'mdi:camera' }, attr.class('w-6 h-6 text-white'))
          )
        ),
        // Info and buttons
        html.div(
          attr.class('flex flex-col gap-2'),
          html.p(
            attr.class('font-medium text-lg'),
            userProfile.map((p) => p?.displayName || '')
          ),
          html.p(
            attr.class('text-sm text-gray-500'),
            userProfile.map((p) => p?.email || '')
          ),
          html.div(
            attr.class('flex gap-2 mt-1'),
            // Hidden file input
            html.input(
              attr.type('file'),
              attr.id('avatar-upload-input'),
              attr.accept('image/jpeg,image/png,image/gif,image/webp'),
              attr.class('hidden'),
              on.change((e) => {
                const input = e.target as HTMLInputElement
                const file = input.files?.[0]
                if (file) {
                  handleAvatarUpload(file)
                  input.value = '' // Reset for re-upload
                }
              })
            ),
            // Upload button
            html.button(
              attr.class(
                isUploadingAvatar.map((loading) =>
                  loading
                    ? 'px-3 py-1.5 text-sm border rounded-md text-gray-400 cursor-not-allowed'
                    : 'px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 transition-colors'
                )
              ),
              attr.disabled(isUploadingAvatar),
              on.click(() => triggerAvatarInput()),
              html.span(
                attr.class('flex items-center gap-1.5'),
                When(
                  isUploadingAvatar,
                  () =>
                    Icon(
                      { icon: 'mdi:loading' },
                      attr.class('w-4 h-4 animate-spin')
                    ),
                  () => Icon({ icon: 'mdi:upload' }, attr.class('w-4 h-4'))
                ),
                isUploadingAvatar.map((loading) =>
                  loading ? 'Uploading...' : 'Upload'
                )
              )
            ),
            // Delete button (only show if avatar exists)
            When(
              userProfile.map((p) => !!p?.avatarUrl),
              () =>
                html.button(
                  attr.class(
                    isDeletingAvatar.map((loading) =>
                      loading
                        ? 'px-3 py-1.5 text-sm border border-red-200 text-red-300 rounded-md cursor-not-allowed'
                        : 'px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors'
                    )
                  ),
                  attr.disabled(isDeletingAvatar),
                  on.click(() => handleAvatarDelete()),
                  html.span(
                    attr.class('flex items-center gap-1.5'),
                    When(
                      isDeletingAvatar,
                      () =>
                        Icon(
                          { icon: 'mdi:loading' },
                          attr.class('w-4 h-4 animate-spin')
                        ),
                      () => Icon({ icon: 'mdi:delete' }, attr.class('w-4 h-4'))
                    ),
                    isDeletingAvatar.map((loading) =>
                      loading ? 'Removing...' : 'Remove'
                    )
                  )
                ),
              () => Empty
            )
          )
        )
      ),

      // Name editing
      html.form(
        on.submit((e) => {
          e.preventDefault()
          nameForm.submit(e)
        }),
        attr.class('space-y-4'),
        html.div(
          attr.class('grid grid-cols-1 md:grid-cols-2 gap-4'),
          html.div(
            Control(TextInput, {
              controller: nameForm.controller.field('firstName'),
              label: 'First Name',
              placeholder: 'Enter first name',
            })
          ),
          html.div(
            Control(TextInput, {
              controller: nameForm.controller.field('lastName'),
              label: 'Last Name',
              placeholder: 'Enter last name',
            })
          )
        ),
        html.div(
          Control(TextInput, {
            controller: nameForm.controller.field('displayName'),
            label: 'Display Name',
            placeholder: 'How your name appears to others (optional)',
          }),
          html.p(
            attr.class('text-xs text-gray-500 mt-1'),
            'Leave empty to use "First Name Last Name"'
          )
        ),
        html.div(
          attr.class('flex gap-2'),
          Button(
            {
              loading: isSavingName,
              disabled: isSavingName,
              type: 'submit',
            },
            isSavingName.map((s) => (s ? 'Saving...' : 'Save Name'))
          )
        )
      ),

      // Email section
      html.div(
        attr.class('border-t pt-6 mt-6'),
        html.h3(attr.class('text-lg font-medium mb-4'), 'Email Address'),
        html.div(
          attr.class('flex items-center gap-4'),
          html.span(userProfile.map((p) => p?.email || '')),
          When(
            userProfile.map((p) => !!p?.hasPassword),
            () =>
              Modal(
                { size: 'md', dismissable: true },
                (openModal, closeModal) =>
                  html.div(
                    // Trigger button
                    html.button(
                      attr.class(
                        'px-3 py-1 border rounded text-sm hover:bg-gray-50'
                      ),
                      on.click(() => {
                        emailForm.controller.change({
                          newEmail: '',
                          password: '',
                        })
                        openModal({
                          header: html.h3(
                            attr.class('text-lg font-semibold text-gray-900'),
                            'Change Email Address'
                          ),
                          body: html.form(
                            on.submit((e) => {
                              e.preventDefault()
                              emailForm.submit(e).then(() => {
                                if (!emailForm.controller.hasError.get()) {
                                  closeModal()
                                }
                              })
                            }),
                            attr.class('space-y-4'),
                            html.p(
                              attr.class('text-sm text-gray-600'),
                              'A verification email will be sent to your new email address. Your email will not change until you click the verification link.'
                            ),
                            Control(TextInput, {
                              controller:
                                emailForm.controller.field('newEmail'),
                              label: 'New Email Address',
                              placeholder: 'Enter new email',
                            }),
                            Control(PasswordInput, {
                              controller:
                                emailForm.controller.field('password'),
                              label: 'Current Password',
                              placeholder: 'Enter your password',
                            }),
                            html.div(
                              attr.class('flex justify-end gap-2'),
                              html.button(
                                attr.class(
                                  'px-4 py-2 border rounded hover:bg-gray-50'
                                ),
                                attr.type('button'),
                                on.click(() => closeModal()),
                                'Cancel'
                              ),
                              Button(
                                {
                                  loading: isSavingEmail,
                                  disabled: isSavingEmail,
                                  type: 'submit',
                                },
                                isSavingEmail.map((s) =>
                                  s ? 'Sending...' : 'Send Verification'
                                )
                              )
                            )
                          ),
                        })
                      }),
                      'Change Email'
                    )
                  )
              ),
            () => Empty
          ),
          When(
            userProfile.map((p) => p !== null && p.hasPassword === false),
            () =>
              html.span(
                attr.class('text-sm text-gray-500'),
                '(Set a password first to change email)'
              ),
            () => Empty
          )
        )
      )
    )
  }

  function renderSecuritySection() {
    const hasPassword = userProfile.map((p) => !!p?.hasPassword)

    return html.div(
      attr.class('space-y-6'),

      // Password section
      html.div(
        html.h3(
          attr.class('text-lg font-medium mb-4'),
          hasPassword.map((h) => (h ? 'Change Password' : 'Add Password'))
        ),
        html.p(
          attr.class('text-sm text-gray-600 mb-4'),
          hasPassword.map((h) =>
            h
              ? 'Enter your current password and a new password to change it.'
              : 'Add a password to enable email/password login as a backup authentication method.'
          )
        ),

        html.form(
          on.submit((e) => {
            e.preventDefault()
            passwordForm.submit(e)
          }),
          attr.class('space-y-4 max-w-md'),
          When(
            hasPassword,
            () =>
              html.div(
                attr.class('space-y-2'),
                Control(PasswordInput, {
                  controller: passwordForm.controller.field('currentPassword'),
                  label: 'Current Password',
                  placeholder: 'Enter current password',
                }),
                html.a(
                  attr.href('/forgot-password'),
                  attr.class('text-sm text-brand-600 hover:text-brand-700'),
                  'Forgot your password?'
                )
              ),
            () => Empty
          ),
          html.div(
            Control(PasswordInput, {
              controller: passwordForm.controller.field('newPassword'),
              label: 'New Password',
              placeholder: 'Enter new password',
            })
          ),
          html.div(
            Control(PasswordInput, {
              controller: passwordForm.controller.field('confirmPassword'),
              label: 'Confirm Password',
              placeholder: 'Confirm new password',
            })
          ),
          Button(
            {
              loading: isSavingPassword,
              disabled: isSavingPassword,
              type: 'submit',
            },
            isSavingPassword.map((s) =>
              s
                ? 'Saving...'
                : hasPassword.get()
                  ? 'Change Password'
                  : 'Set Password'
            )
          )
        )
      )
    )
  }

  function renderConnectedAccountsSection() {
    return html.div(
      attr.class('space-y-6'),

      html.h3(attr.class('text-lg font-medium mb-4'), 'Connected Accounts'),
      html.p(
        attr.class('text-sm text-gray-600 mb-4'),
        'Manage the OAuth providers linked to your account.'
      ),

      When(
        isLoadingIdentities,
        () => LoadingState({ size: 'sm' }),
        () => Empty
      ),

      Unless(isLoadingIdentities, () =>
        html.div(
          attr.class('space-y-3'),
          ForEach(identities, (identity) =>
            html.div(
              attr.class(
                'flex items-center justify-between p-4 border rounded-lg'
              ),
              html.div(
                attr.class('flex items-center gap-3'),
                Icon({ icon: getProviderIcon(identity.get().provider) }),
                html.div(
                  html.p(
                    attr.class('font-medium capitalize'),
                    identity.map((i) => i.provider)
                  ),
                  html.p(
                    attr.class('text-sm text-gray-500'),
                    identity.map((i) => i.email || i.displayName || '')
                  )
                )
              ),
              When(
                identities.map(
                  (ids) => ids.length > 1 || !!userProfile.get()?.hasPassword
                ),
                () =>
                  html.button(
                    attr.class(
                      'px-3 py-1 text-sm text-gray-600 hover:text-gray-800'
                    ),
                    on.click(() =>
                      handleUnlinkIdentity(identity.get().provider)
                    ),
                    'Unlink'
                  ),
                () => Empty
              )
            )
          ),
          When(
            identities.map((ids) => ids.length === 0),
            () =>
              html.p(
                attr.class('text-gray-500 text-center py-4'),
                'No connected accounts'
              ),
            () => Empty
          )
        )
      )
    )
  }

  function getProviderIcon(provider: string): string {
    switch (provider) {
      case 'google':
        return 'mdi:google'
      case 'github':
        return 'mdi:github'
      case 'microsoft':
        return 'mdi:microsoft'
      case 'gitlab':
        return 'mdi:gitlab'
      case 'bitbucket':
        return 'mdi:bitbucket'
      default:
        return 'mdi:account'
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-2'),
      Icon({ icon: 'mdi:account-circle' }),
      html.span('Profile Settings')
    ),
    content: Fragment(
      When(
        isLoading,
        () => LoadingState({ size: 'lg' }),
        () => Empty
      ),
      Ensure(error, () => ErrorState({ error })),
      Unless(isLoading, () =>
        Unless(
          error.map((e) => e !== null),
          () =>
            Tabs({
              items: [
                {
                  key: 'profile' as const,
                  label: 'Profile',
                  content: renderProfileSection,
                },
                {
                  key: 'security' as const,
                  label: 'Security',
                  content: renderSecuritySection,
                },
                {
                  key: 'connected' as const,
                  label: 'Connected Accounts',
                  content: renderConnectedAccountsSection,
                },
              ],
              value: activeTab,
              onChange: handleTabChange,
            })
        )
      )
    ),
  })
}
